import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ClipboardList,
  Info,
  KeyRound,
  LayoutGrid,
  Lock,
  RotateCcw,
  User,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { AC } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import MainTabAccessMatrix from "@/components/access/MainTabAccessMatrix";
import SubtabAccessMatrix from "@/components/access/SubtabAccessMatrix";
import UserPermissionsGrid from "@/components/access/UserPermissionsGrid";
import {
  USER_CLASSES,
  employeeOptionLabel,
  emptyMatrix,
  setCell,
} from "@/lib/subtabAccess";
import {
  getOrgSubtabDefaults,
  getUserSubtabAccess,
  listAccessEmployees,
  resetUserSubtabAccess,
  saveOrgSubtabDefaults,
  saveUserSubtabAccess,
} from "@/lib/subtabAccessApi";
import { emptyMainTabMatrix, setMainTabCell } from "@/lib/maintabAccess";
import {
  getOrgMaintabDefaults,
  getUserMaintabAccess,
  resetUserMaintabAccess,
  saveOrgMaintabDefaults,
  saveUserMaintabAccess,
} from "@/lib/maintabAccessApi";
import "@/styles/emr-catalog.css";

const TABS = [
  {
    id: "defaults",
    label: "User Class Defaults",
    subtitle: "Front Desk / Clinical / Billing",
    icon: Users,
    testId: AC.tabDefaults,
  },
  {
    id: "users",
    label: "Individual Users",
    subtitle: "Customize per user",
    icon: User,
    testId: AC.tabUsers,
  },
  {
    id: "audit",
    label: "Access Audit",
    subtitle: "View access changes",
    icon: ClipboardList,
    testId: AC.tabAudit,
    disabled: true,
  },
];

function InfoBanner({ children, testId }) {
  return (
    <div className="ac-info" data-testid={testId}>
      <Info className="ac-info-icon" aria-hidden="true" />
      <p>{children}</p>
    </div>
  );
}

function classLabel(id) {
  return USER_CLASSES.find((item) => item.id === id)?.label || id;
}

export default function AccessControlPage() {
  const { context } = useAuth();
  const organizationId = context?.organization?.id;
  const [tab, setTab] = useState("defaults");
  const [userClass, setUserClass] = useState("front_desk");
  const [defaultMatrix, setDefaultMatrix] = useState(() => emptyMatrix());
  const [defaultMainTabs, setDefaultMainTabs] = useState(() => emptyMainTabMatrix());
  const [defaultsLoading, setDefaultsLoading] = useState(true);
  const [defaultsSaving, setDefaultsSaving] = useState(false);

  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState("");
  const [employee, setEmployee] = useState(null);
  const [userMatrix, setUserMatrix] = useState(() => emptyMatrix());
  const [userMainTabs, setUserMainTabs] = useState(() => emptyMainTabMatrix());
  const [userDefaultMainTabs, setUserDefaultMainTabs] = useState(() => emptyMainTabMatrix());
  const [userLoading, setUserLoading] = useState(false);
  const [userSaving, setUserSaving] = useState(false);
  const [hasOverride, setHasOverride] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [workspace, setWorkspace] = useState("module");
  const [roleHelpOpen, setRoleHelpOpen] = useState(false);

  const loadDefaults = useCallback(async (classId) => {
    setDefaultsLoading(true);
    try {
      const [subtabs, maintabs] = await Promise.all([
        getOrgSubtabDefaults({ userClass: classId, organizationId }),
        getOrgMaintabDefaults({ userClass: classId, organizationId }),
      ]);
      setDefaultMatrix(subtabs.matrix || emptyMatrix());
      setDefaultMainTabs(maintabs.matrix || emptyMainTabMatrix());
    } catch {
      toast.error("Failed to load organization default access.");
      setDefaultMatrix(emptyMatrix());
      setDefaultMainTabs(emptyMainTabMatrix());
    } finally {
      setDefaultsLoading(false);
    }
  }, [organizationId]);

  const loadEmployees = useCallback(async () => {
    try {
      const data = await listAccessEmployees({ organizationId });
      setEmployees(data.items || []);
    } catch {
      toast.error("Failed to load employees.");
      setEmployees([]);
    }
  }, [organizationId]);

  const loadUser = useCallback(async (id) => {
    if (!id) {
      setEmployee(null);
      setUserMatrix(emptyMatrix());
      setUserMainTabs(emptyMainTabMatrix());
      setUserDefaultMainTabs(emptyMainTabMatrix());
      setHasOverride(false);
      return;
    }
    setUserLoading(true);
    try {
      const [subtabs, maintabs] = await Promise.all([
        getUserSubtabAccess(id, { organizationId }),
        getUserMaintabAccess(id, { organizationId }),
      ]);
      setEmployee(subtabs.employee || maintabs.employee || null);
      setUserMatrix(subtabs.matrix || emptyMatrix());
      setUserMainTabs(maintabs.matrix || emptyMainTabMatrix());
      setUserDefaultMainTabs(maintabs.defaultMatrix || emptyMainTabMatrix());
      setHasOverride(Boolean(subtabs.hasOverride || maintabs.hasOverride));
    } catch {
      toast.error("Failed to load user access.");
    } finally {
      setUserLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    loadDefaults(userClass);
  }, [loadDefaults, userClass]);

  useEffect(() => {
    if (tab === "users") loadEmployees();
  }, [tab, loadEmployees]);

  useEffect(() => {
    if (tab === "users") loadUser(employeeId);
  }, [tab, employeeId, loadUser]);

  const headerCopy = useMemo(() => {
    if (workspace === "permissions") {
      return {
        title: "Access Control",
        subtitle: "Configure what each user class can do within the system.",
      };
    }
    if (tab === "users") {
      return {
        title: "Access Control",
        subtitle: "Customize Main Tab and subtab access for an individual user.",
      };
    }
    return {
      title: "Access Control",
      subtitle:
        "Set default Main Tab and subtab access for each user class. These settings will apply to new users in the organization.",
    };
  }, [tab, workspace]);

  const saveDefaults = async () => {
    setDefaultsSaving(true);
    try {
      const [subtabs, maintabs] = await Promise.all([
        saveOrgSubtabDefaults({
          userClass,
          organizationId,
          matrix: defaultMatrix,
        }),
        saveOrgMaintabDefaults({
          userClass,
          organizationId,
          matrix: defaultMainTabs,
        }),
      ]);
      setDefaultMatrix(subtabs.matrix || defaultMatrix);
      setDefaultMainTabs(maintabs.matrix || defaultMainTabs);
      toast.success("Default access saved.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to save default access.");
    } finally {
      setDefaultsSaving(false);
    }
  };

  const saveUser = async () => {
    if (!employeeId) return;
    setUserSaving(true);
    try {
      const [subtabs, maintabs] = await Promise.all([
        saveUserSubtabAccess(employeeId, {
          organizationId,
          matrix: userMatrix,
        }),
        saveUserMaintabAccess(employeeId, {
          organizationId,
          matrix: userMainTabs,
        }),
      ]);
      setUserMatrix(subtabs.matrix || userMatrix);
      setUserMainTabs(maintabs.matrix || userMainTabs);
      setUserDefaultMainTabs(maintabs.defaultMatrix || userDefaultMainTabs);
      setHasOverride(Boolean(subtabs.hasOverride || maintabs.hasOverride));
      toast.success("User access saved.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to save user access.");
    } finally {
      setUserSaving(false);
    }
  };

  const confirmReset = async () => {
    if (!employeeId) return;
    setUserSaving(true);
    try {
      const [subtabs, maintabs] = await Promise.all([
        resetUserSubtabAccess(employeeId, { organizationId }),
        resetUserMaintabAccess(employeeId, { organizationId }),
      ]);
      setUserMatrix(subtabs.matrix || emptyMatrix());
      setUserMainTabs(maintabs.matrix || emptyMainTabMatrix());
      setUserDefaultMainTabs(maintabs.defaultMatrix || emptyMainTabMatrix());
      setHasOverride(Boolean(subtabs.hasOverride || maintabs.hasOverride));
      setResetOpen(false);
      toast.success("User access restored to the organization default.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to reset user access.");
    } finally {
      setUserSaving(false);
    }
  };

  return (
    <div data-testid={AC.page} className="emr-catalog emr-catalog--access emr-catalog--full-table">
      <div className="emr-catalog__pagehead">
        <div>
          <h1 data-testid={AC.title} className="emr-catalog__title">{headerCopy.title}</h1>
          <p className="emr-catalog__subtitle">{headerCopy.subtitle}</p>
        </div>
        <Button
          type="button"
          variant="outline"
          data-testid={AC.roleDefinitions}
          onClick={() => setRoleHelpOpen(true)}
          className="btn emr-catalog__btn emr-catalog__btn--ghost ac-role-help"
        >
          <Users className="emr-catalog__btn-icon" />
          View Role Definitions
        </Button>
      </div>

      <section className="card emr-catalog__section">
        <div className="ac-workspace-tabs" role="tablist" aria-label="Access Control workspaces">
          <button
            type="button"
            role="tab"
            aria-selected={workspace === "module"}
            data-testid={AC.workspaceModule}
            className={`ac-workspace-tab${workspace === "module" ? " is-active" : ""}`}
            onClick={() => setWorkspace("module")}
          >
            <LayoutGrid className="h-5 w-5" aria-hidden="true" />
            <span>
              <span className="ac-tab-label">Module Access Matrix</span>
              <span className="ac-tab-sub">Where users can go and what they can do (Read / Write)</span>
            </span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={workspace === "permissions"}
            data-testid={AC.workspacePermissions}
            className={`ac-workspace-tab${workspace === "permissions" ? " is-active" : ""}`}
            onClick={() => setWorkspace("permissions")}
          >
            <KeyRound className="h-5 w-5" aria-hidden="true" />
            <span>
              <span className="ac-tab-label">User Permissions</span>
              <span className="ac-tab-sub">Configure what each user class can do within the system.</span>
            </span>
          </button>
        </div>
      </section>

      {workspace === "permissions" ? (
        <div className="emr-catalog__workspace">
          <UserPermissionsGrid />
        </div>
      ) : (
      <>
      <section className="card emr-catalog__section">
        <h2 className="emr-catalog__section-title">Sections</h2>
        <div className="ac-tabs" role="tablist" aria-label="Access Control sections">
          {TABS.map((item) => {
            const Icon = item.icon;
            const active = tab === item.id;
            return (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={active}
                disabled={item.disabled}
                data-testid={item.testId}
                className={`ac-tab${active ? " is-active" : ""}${item.disabled ? " is-disabled" : ""}`}
                onClick={() => {
                  if (!item.disabled) setTab(item.id);
                }}
              >
                <Icon className="h-5 w-5" aria-hidden="true" />
                <span>
                  <span className="ac-tab-label">{item.label}</span>
                  <span className="ac-tab-sub">{item.subtitle}</span>
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {tab === "defaults" ? (
        <div className="emr-catalog__workspace">
          <section className="card emr-catalog__section">
            <h2 className="emr-catalog__section-title">Organization Default Access (by User Class)</h2>
            <div className="ac-toolbar">
              <label className="ac-field">
                <span>User Class</span>
                <select
                  className="browser-default emr-catalog__select"
                  data-testid={AC.userClassSelect}
                  value={userClass}
                  onChange={(event) => setUserClass(event.target.value)}
                >
                  {USER_CLASSES.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>
              <InfoBanner>
                These default settings will be applied to all new users assigned to the{" "}
                {classLabel(userClass)} user class.
              </InfoBanner>
            </div>
          </section>

          {defaultsLoading ? (
            <section className="card emr-catalog__section">
              <p className="ac-loading">Loading default access…</p>
            </section>
          ) : (
            <>
              <section className="card emr-catalog__section">
                <h2 className="emr-catalog__section-title">Main Tab Defaults</h2>
                <MainTabAccessMatrix
                  matrix={defaultMainTabs}
                  onChange={(tabId, level) =>
                    setDefaultMainTabs((current) => setMainTabCell(current, tabId, level))
                  }
                />
              </section>
              <section className="card emr-catalog__section">
                <h2 className="emr-catalog__section-title">Chart Subtab Defaults</h2>
                <SubtabAccessMatrix
                  matrix={defaultMatrix}
                  onChange={(areaId, subtabId, level) =>
                    setDefaultMatrix((current) => setCell(current, areaId, subtabId, level))
                  }
                />
              </section>
            </>
          )}

          <section className="card emr-catalog__section">
            <footer className="ac-footer">
              <InfoBanner>
                These are organization-wide defaults. Individual users can still be customized
                on the Individual Users tab.
              </InfoBanner>
              <Button
                data-testid={AC.saveDefaults}
                onClick={saveDefaults}
                disabled={defaultsLoading || defaultsSaving}
                className="btn emr-catalog__btn ac-save ac-save--default"
              >
                <Lock className="emr-catalog__btn-icon" />
                Save Default Access
              </Button>
            </footer>
          </section>
        </div>
      ) : (
        <div className="emr-catalog__workspace">
          <section className="card emr-catalog__section">
            <h2 className="emr-catalog__section-title">Individual User Access</h2>
            <div className="ac-toolbar ac-toolbar--user">
              <label className="ac-field">
                <span>Employee</span>
                <select
                  className="browser-default emr-catalog__select"
                  data-testid={AC.employeeSelect}
                  value={employeeId}
                  onChange={(event) => setEmployeeId(event.target.value)}
                >
                  <option value="">Select an employee…</option>
                  {employees.map((item) => (
                    <option key={item.id} value={item.id}>
                      {employeeOptionLabel(item)}
                    </option>
                  ))}
                </select>
              </label>
              {employee ? (
                <div className="ac-employee" data-testid={AC.employeeCard}>
                  <div>
                    <span>Name</span>
                    <strong data-testid={AC.employeeName}>{employee.name || "—"}</strong>
                  </div>
                  <div>
                    <span>Username</span>
                    <strong data-testid={AC.employeeUsername}>{employee.username || "—"}</strong>
                  </div>
                  <div>
                    <span>User Class</span>
                    <strong data-testid={AC.employeeClass}>{employee.userClassLabel || "—"}</strong>
                  </div>
                  <div>
                    <span>Role</span>
                    <strong data-testid={AC.employeeRole}>{employee.roleLabel || "—"}</strong>
                  </div>
                  <div>
                    <span>Status</span>
                    <strong
                      data-testid={AC.employeeStatus}
                      className={employee.active ? "ac-status-active" : "ac-status-inactive"}
                    >
                      {employee.active ? "Active" : "Inactive"}
                    </strong>
                  </div>
                </div>
              ) : null}
            </div>
          </section>

          <section className="card emr-catalog__section">
            {employee ? (
              <InfoBanner>
                Adjust Main Tab and subtab access for this user. Changes will only affect this
                user and will override the organization defaults.
              </InfoBanner>
            ) : (
              <InfoBanner>Select an employee to customize Main Tab and subtab access.</InfoBanner>
            )}
          </section>

          {userLoading ? (
            <section className="card emr-catalog__section">
              <p className="ac-loading">Loading user access…</p>
            </section>
          ) : employee ? (
            <>
              <section className="card emr-catalog__section">
                <h2 className="emr-catalog__section-title">Main Tabs</h2>
                <MainTabAccessMatrix
                  matrix={userMainTabs}
                  defaultMatrix={userDefaultMainTabs}
                  hasOverride={hasOverride}
                  onChange={(tabId, level) =>
                    setUserMainTabs((current) => setMainTabCell(current, tabId, level))
                  }
                />
              </section>
              <section className="card emr-catalog__section">
                <h2 className="emr-catalog__section-title">Chart Subtabs</h2>
                <SubtabAccessMatrix
                  matrix={userMatrix}
                  onChange={(areaId, subtabId, level) =>
                    setUserMatrix((current) => setCell(current, areaId, subtabId, level))
                  }
                />
              </section>
            </>
          ) : null}

          <section className="card emr-catalog__section">
            <footer className="ac-footer ac-footer--user">
              <Button
                type="button"
                variant="outline"
                data-testid={AC.resetUser}
                disabled={!employeeId || userLoading || userSaving}
                onClick={() => setResetOpen(true)}
                className="btn emr-catalog__btn emr-catalog__btn--ghost ac-reset"
              >
                <RotateCcw className="emr-catalog__btn-icon" />
                Reset to Organization Default
              </Button>
              <Button
                data-testid={AC.saveUser}
                onClick={saveUser}
                disabled={!employeeId || userLoading || userSaving}
                className="btn emr-catalog__btn ac-save ac-save--user"
              >
                <Lock className="emr-catalog__btn-icon" />
                Save User Access
              </Button>
            </footer>
            {employee && !hasOverride ? (
              <p className="ac-footnote">This user is currently using the organization default access.</p>
            ) : null}
          </section>
        </div>
      )}
      </>
      )}

      <AlertDialog open={roleHelpOpen} onOpenChange={setRoleHelpOpen}>
        <AlertDialogContent data-testid={AC.roleDefinitionsDialog} className="ac-role-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Role definitions</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="ac-role-copy">
                <p>
                  EMRbyMD Access Control has three User Classes: Front Desk, Clinical, and Billing.
                  Administrator is not a User Class.
                </p>
                <p>
                  <strong>User Class</strong> sets default module access (No Access / Read / Write).
                </p>
                <p>
                  <strong>Individual Override</strong> replaces those defaults for one user.
                  Effective Access equals Individual Override when present; otherwise User Class
                  access; otherwise Access Control is unconfigured and Phase 2 fallback applies.
                </p>
                <p>
                  <strong>Role</strong> (Physician, Nurse, NP, PA, Medical Assistant, Front Desk,
                  Biller, Administrator) is an identity used for capabilities and provenance.
                  Clinical roles share the Clinical User Class; they do not get extra User Classes.
                </p>
                <p>
                  <strong>Capability</strong> authorizes a privileged action in addition to module
                  access. WRITE never implies Sign, Amend, Close, or Assess/Plan.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>Close</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
        <AlertDialogContent data-testid={AC.resetDialog}>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Access?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove this user&apos;s customized access and restore the organization
              default access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={AC.resetCancel}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid={AC.resetConfirm}
              onClick={confirmReset}
              className="bg-teal-700 hover:bg-teal-800"
            >
              Reset
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export { TABS };
